import { LessonType } from 'webclient.constants';
import {
  UNIT_RUBRIC_DOCUMENT,
  PDF_FORMAT,
  ASSESSMENT_WITH_DIFFERENTIATION_DOCUMENT,
  SELF_ASSESSMENT_DOCUMENT,
  ANSWER_SHEET_DOCUMENT,
  WORKSHEET_DOCUMENT,
  LESSON_PLAN_DOCUMENT,
  AE_RUBRIC_DOCUMENT,
  VICTORIAN_RUBRIC_DOCUMENT,
  SOCIAL_AND_EMOTIONAL_DOCUMENT,
  SUPPORT_DOCUMENT,
  EXTENSION_DOCUMENT,
  ASSESSMENT_FOR_LEARNING_TOOL,
  LESSON,
  ASSESSMENT,
  SUMMATIVE_ASSESSMENT_DOCUMENT,
  FORMATIVE_ASSESSMENT_DOCUMENT,
  VOCABULARY_EN_DOCUMENT,
  VOCABULARY_ES_DOCUMENT,
  TEACHER_INFORMATION_DOCUMENT,
  LESSON_RUBRIC_DOCUMENT,
  REGION_AU,
  REGION_US,
  BASIC_ASSESSMENT_DOCUMENT,
} from 'global/constants';
import {
  EVENT_CATEGORY_DIFFERENTIATED_ASSESSMENT,
  EVENT_CATEGORY_SELF_ASSESSMENT,
  EVENT_CATEGORY_ANSWER_SHEET,
  EVENT_CATEGORY_LESSON,
  EVENT_CATEGORY_LESSON_PLAN,
  EVENT_CATEGORY_WORKSHEETS,
  EVENT_CATEGORY_SUPPORT_DOCUMENT,
  EVENT_CATEGORY_EXTENSION_DOCUMENT,
  EVENT_CATEGORY_ASSESSMENT_FOR_LEARNING_TOOL,
  EVENT_CATEGORY_SUMMATIVE_ASSESSMENT,
  EVENT_CATEGORY_FORMATIVE_ASSESSMENT,
  EVENT_CATEGORY_VOCABULARY_EN_DOCUMENT,
  EVENT_CATEGORY_VOCABULARY_ES_DOCUMENT,
  EVENT_CATEGORY_TEACHER_INFORMATION_DOCUMENT,
  EVENT_CATEGORY_LESSON_RUBRIC_DOCUMENT,
  EVENT_CATEGORY_ASSESSMENT,
} from 'approot/shared/data-tracker/data-events.constants';
import { LessonWorksheetFragment_worksheet } from './sections/lesson-worksheets/__generated__/LessonWorksheetFragment';
import {
  AssessmentWorksheetsFragment_assessmentDoc,
  AssessmentWorksheetsFragment_formativeAssessment,
  AssessmentWorksheetsFragment_selfAssessment,
  AssessmentWorksheetsFragment_summativeAssessment,
} from 'approot/assessment/sections/worksheets/__generated__/AssessmentWorksheetsFragment';
import {
  LessonPrintablesFragment_printableLessonPlan,
  LessonPrintablesFragment_printableLessonRubricSheet,
  LessonPrintablesFragment_printableTeacherInformationSheet,
  LessonPrintablesFragment_printableVocabularyEnSheet,
  LessonPrintablesFragment_printableVocabularyEsSheet,
} from './sections/lesson-printables/__generated__/LessonPrintablesFragment';
import { StudentLessonWorksheetsFragment_worksheet } from 'approot/student-lesson/sections/worksheets/__generated__/StudentLessonWorksheetsFragment';
import { StudentAssessmentWorksheetsFragment_assessmentDoc } from 'approot/student-assessment/sections/worksheets/__generated__/StudentAssessmentWorksheetsFragment';
import {
  GetLessonById_getLessonById_worksheet,
  GetLessonById_getLessonById_printableLessonPlan,
  GetLessonById_getLessonById_printableWorksheet,
  GetLessonById_getLessonById_printableAnswerSheet,
  GetLessonById_getLessonById_printableSupportSheet,
  GetLessonById_getLessonById_printableExtensionSheet,
  GetLessonById_getLessonById_printableVocabularyEnSheet,
  GetLessonById_getLessonById_printableVocabularyEsSheet,
  GetLessonById_getLessonById_printableTeacherInformationSheet,
  GetLessonById_getLessonById_printableLessonRubricSheet,
} from './__generated__/GetLessonById';
import {
  GetAssessmentById_getAssessmentById_answerSheet,
  GetAssessmentById_getAssessmentById_assessmentDoc,
  GetAssessmentById_getAssessmentById_selfAssessment,
  GetAssessmentById_getAssessmentById_unitRubric,
  GetAssessmentById_getAssessmentById_aeRubric,
  GetAssessmentById_getAssessmentById_victorianRubric,
  GetAssessmentById_getAssessmentById_assessmentForLearningTool,
  GetAssessmentById_getAssessmentById_formativeAssessment,
  GetAssessmentById_getAssessmentById_summativeAssessment,
  GetAssessmentById_getAssessmentById_basicAssessment,
} from 'approot/assessment/__generated__/GetAssessmentById';
import {
  AssessmentRubricsFragment_unitRubric,
  AssessmentRubricsFragment_aeRubric,
  AssessmentRubricsFragment_victorianRubric,
} from 'approot/assessment/sections/rubrics/__generated__/AssessmentRubricsFragment';
import { AssessmentForLearningToolFragment_assessmentForLearningTool } from 'approot/assessment/sections/assessment-for-learning-tool/__generated__/AssessmentForLearningToolFragment';
import {
  StudentLessonPrintablesFragment_printableVocabularyEnSheet,
  StudentLessonPrintablesFragment_printableVocabularyEsSheet,
} from 'approot/student-lesson/sections/printables/__generated__/StudentLessonPrintablesFragment';

export const SHEET_GROUP_MAIN = 'main';
export const SHEET_GROUP_PRINTABLES = 'printables';
export const SHEET_GROUP_RUBRICS = 'rubrics';
export type SheetGroup =
  | typeof SHEET_GROUP_MAIN
  | typeof SHEET_GROUP_PRINTABLES
  | typeof SHEET_GROUP_RUBRICS;

export type SheetPreviewRatio = {
  [key in SheetGroup]?: number;
};

export type LessonSheets = {
  [key in
    | LessonSheetKey
    | AssessmentSheetKey
    | AssessmentRubricKey
    | AssessmentLearningToolKey]?:
    | LessonWorksheetFragment_worksheet
    | LessonPrintablesFragment_printableVocabularyEnSheet
    | LessonPrintablesFragment_printableVocabularyEsSheet
    | LessonPrintablesFragment_printableTeacherInformationSheet
    | LessonPrintablesFragment_printableLessonRubricSheet
    | StudentLessonWorksheetsFragment_worksheet
    | StudentLessonPrintablesFragment_printableVocabularyEnSheet
    | StudentLessonPrintablesFragment_printableVocabularyEsSheet
    | LessonPrintablesFragment_printableLessonPlan
    | AssessmentWorksheetsFragment_assessmentDoc
    | StudentAssessmentWorksheetsFragment_assessmentDoc
    | AssessmentWorksheetsFragment_selfAssessment
    | AssessmentWorksheetsFragment_summativeAssessment
    | AssessmentWorksheetsFragment_formativeAssessment
    | AssessmentRubricsFragment_unitRubric
    | AssessmentRubricsFragment_aeRubric
    | AssessmentRubricsFragment_victorianRubric
    | AssessmentForLearningToolFragment_assessmentForLearningTool
    | null;
};

type LessonData = {
  id: number;
  type: LessonType;
  sheetPreviewRatio?: SheetPreviewRatio;
};

export type LessonSheetData = LessonData & LessonSheets;

export type LessonSheetKey =
  | 'worksheet'
  | 'printableLessonPlan'
  | 'printableWorksheet'
  | 'printableAnswerSheet'
  | 'printableSupportSheet'
  | 'printableExtensionSheet'
  | 'printableVocabularyEnSheet'
  | 'printableVocabularyEsSheet'
  | 'printableTeacherInformationSheet'
  | 'printableLessonRubricSheet';
export type AssessmentSheetKey =
  | 'assessmentDoc'
  | 'selfAssessment'
  | 'basicAssessment'
  | 'summativeAssessment'
  | 'formativeAssessment'
  | 'answerSheet';

export type AssessmentLearningToolKey = 'assessmentForLearningTool';

export type AssessmentRubricKey =
  | 'unitRubric'
  | 'aeRubric'
  | 'victorianRubric'
  | 'socialAndEmotionalRubric';

export type AnyObjectWithDocument = {
  worksheet?: GetLessonById_getLessonById_worksheet | null;
  printableLessonPlan?: GetLessonById_getLessonById_printableLessonPlan | null;
  printableWorksheet?: GetLessonById_getLessonById_printableWorksheet | null;
  printableAnswerSheet?: GetLessonById_getLessonById_printableAnswerSheet | null;
  printableSupportSheet?: GetLessonById_getLessonById_printableSupportSheet | null;
  printableExtensionSheet?: GetLessonById_getLessonById_printableExtensionSheet | null;
  printableVocabularyEnSheet?: GetLessonById_getLessonById_printableVocabularyEnSheet | null;
  printableVocabularyEsSheet?: GetLessonById_getLessonById_printableVocabularyEsSheet | null;
  printableTeacherInformationSheet?: GetLessonById_getLessonById_printableTeacherInformationSheet | null;
  printableLessonRubricSheet?: GetLessonById_getLessonById_printableLessonRubricSheet | null;
  assessmentDoc?: GetAssessmentById_getAssessmentById_assessmentDoc | null;
  basicAssessment?: GetAssessmentById_getAssessmentById_basicAssessment | null;
  summativeAssessment?: GetAssessmentById_getAssessmentById_summativeAssessment | null;
  formativeAssessment?: GetAssessmentById_getAssessmentById_formativeAssessment | null;
  selfAssessment?: GetAssessmentById_getAssessmentById_selfAssessment | null;
  answerSheet?: GetAssessmentById_getAssessmentById_answerSheet | null;
  unitRubric?: GetAssessmentById_getAssessmentById_unitRubric | null;
  aeRubric?: GetAssessmentById_getAssessmentById_aeRubric | null;
  victorianRubric?: GetAssessmentById_getAssessmentById_victorianRubric | null;
  assessmentForLearningTool?: GetAssessmentById_getAssessmentById_assessmentForLearningTool | null;
};

type CommonData = {
  name: string;
  dataEvent: string;
  displaySingle: boolean;
  hasSort?: boolean;
  addButtonText: string;
  deleteButtonText: string;
  deleteAlertText?: string;
  deleteAlertTextSecondary?: string;
  placeholderText: string;
  documentFormat?: typeof PDF_FORMAT;
  canBookmark?: boolean;
  regions?: string[];
};

export type LessonPagesData = {
  key: LessonSheetKey;
  documentType?: undefined;
} & CommonData;

export type LessonPrintableData = {
  key: LessonSheetKey;
  documentType:
    | typeof WORKSHEET_DOCUMENT
    | typeof LESSON_PLAN_DOCUMENT
    | typeof ANSWER_SHEET_DOCUMENT
    | typeof SUPPORT_DOCUMENT
    | typeof EXTENSION_DOCUMENT
    | typeof VOCABULARY_EN_DOCUMENT
    | typeof VOCABULARY_ES_DOCUMENT
    | typeof TEACHER_INFORMATION_DOCUMENT
    | typeof LESSON_RUBRIC_DOCUMENT;
} & CommonData;

export type AssessmentDocumentData = {
  key: AssessmentSheetKey;
  documentType:
    | typeof UNIT_RUBRIC_DOCUMENT
    | typeof ASSESSMENT_WITH_DIFFERENTIATION_DOCUMENT
    | typeof BASIC_ASSESSMENT_DOCUMENT
    | typeof SELF_ASSESSMENT_DOCUMENT
    | typeof SUMMATIVE_ASSESSMENT_DOCUMENT
    | typeof FORMATIVE_ASSESSMENT_DOCUMENT
    | typeof ANSWER_SHEET_DOCUMENT;
  documentFormat?: typeof PDF_FORMAT;
} & CommonData;

export type AssessmentForLearningToolData = {
  key: AssessmentLearningToolKey;
  documentType: typeof ASSESSMENT_FOR_LEARNING_TOOL;
  documentFormat?: typeof PDF_FORMAT;
} & CommonData;

export type AssessmentRubricsData = {
  key: AssessmentRubricKey;
  documentType:
    | typeof UNIT_RUBRIC_DOCUMENT
    | typeof AE_RUBRIC_DOCUMENT
    | typeof VICTORIAN_RUBRIC_DOCUMENT
    | typeof SOCIAL_AND_EMOTIONAL_DOCUMENT;
  documentFormat?: typeof PDF_FORMAT;
} & CommonData;

export type DocumentData =
  | LessonPagesData
  | LessonPrintableData
  | AssessmentDocumentData
  | AssessmentRubricsData
  | AssessmentForLearningToolData;

export const SHEETS_LIST: {
  [LESSON]: LessonPagesData[];
  [ASSESSMENT]: AssessmentDocumentData[];
} = {
  [LESSON]: [
    {
      key: 'worksheet',
      name: 'PDF',
      dataEvent: EVENT_CATEGORY_LESSON,
      displaySingle: false,
      hasSort: true,
      addButtonText: 'Add Lesson',
      deleteButtonText: 'Delete Lesson',
      placeholderText: 'Drag and drop a Lesson here to upload it.',
      canBookmark: true,
    },
  ],
  [ASSESSMENT]: [
    {
      key: 'assessmentDoc',
      name: 'Assessment with Differentiation',
      dataEvent: EVENT_CATEGORY_DIFFERENTIATED_ASSESSMENT,
      displaySingle: false,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText:
        'Are you sure you want to delete this assessment with differentiation?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new assessment with differentiation is added.',
      placeholderText:
        'Drag and drop an Assessment with Differentiation here to upload it.',
      documentType: ASSESSMENT_WITH_DIFFERENTIATION_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: true,
    },
    {
      key: 'selfAssessment',
      name: 'Self-Assessment',
      dataEvent: EVENT_CATEGORY_SELF_ASSESSMENT,
      displaySingle: true,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText: 'Are you sure you want to delete this Self-Assessment?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new Self-Assessment is added.',
      placeholderText: 'Drag and drop a Self-Assessment here to upload it',
      documentType: SELF_ASSESSMENT_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: true,
    },
    {
      key: 'basicAssessment',
      name: 'Assessment',
      dataEvent: EVENT_CATEGORY_ASSESSMENT,
      displaySingle: false,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText: 'Are you sure you want to delete this assessment?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new assessment is added.',
      placeholderText: 'Drag and drop an Assessment here to upload it.',
      documentType: BASIC_ASSESSMENT_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: true,
      regions: [REGION_AU],
    },
    {
      key: 'summativeAssessment',
      name: 'Summative Assessment',
      dataEvent: EVENT_CATEGORY_SUMMATIVE_ASSESSMENT,
      displaySingle: false,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText:
        'Are you sure you want to delete this summative assessment?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new summative assessment is added.',
      placeholderText: 'Drag and drop a Summative Assessment here to upload it',
      documentType: SUMMATIVE_ASSESSMENT_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: true,
      regions: [REGION_US],
    },
    {
      key: 'formativeAssessment',
      name: 'Formative Assessment',
      dataEvent: EVENT_CATEGORY_FORMATIVE_ASSESSMENT,
      displaySingle: false,
      addButtonText: 'Add',

      deleteButtonText: 'Delete',
      deleteAlertText:
        'Are you sure you want to delete this formative assessment?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new formative assessment is added.',
      placeholderText: 'Drag and drop a Formative Assessment here to upload it',
      documentType: FORMATIVE_ASSESSMENT_DOCUMENT,
      documentFormat: PDF_FORMAT,
      canBookmark: true,
      regions: [REGION_US],
    },
    {
      key: 'answerSheet',
      name: 'Answers',
      dataEvent: EVENT_CATEGORY_ANSWER_SHEET,
      displaySingle: true,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText: 'Are you sure you want to delete these answers?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until new answers are added.',
      placeholderText: 'Drag and drop an Answer Sheet here to upload it.',
      documentType: ANSWER_SHEET_DOCUMENT,
      documentFormat: PDF_FORMAT,
    },
  ],
};

export const ASSESSMENT_FOR_LEARNING_TOOL_LIST: {
  [ASSESSMENT]: AssessmentForLearningToolData[];
} = {
  [ASSESSMENT]: [
    {
      key: 'assessmentForLearningTool',
      name: 'Assessment for Learning Tool',
      dataEvent: EVENT_CATEGORY_ASSESSMENT_FOR_LEARNING_TOOL,
      displaySingle: false,
      addButtonText: 'Add',
      deleteButtonText: 'Delete',
      deleteAlertText:
        'Are you sure you want to delete this assessment for learning tool?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new assessment for learning tool is added.',
      placeholderText:
        'Drag and drop an Assessment for Learning Tool here to upload it.',
      documentType: ASSESSMENT_FOR_LEARNING_TOOL,
      documentFormat: PDF_FORMAT,
      canBookmark: false,
    },
  ],
};

export const PRINTABLES_LIST: {
  [LESSON]: LessonPrintableData[];
  [ASSESSMENT]: AssessmentDocumentData[];
} = {
  [LESSON]: [
    {
      key: 'printableLessonPlan',
      name: 'Lesson plan',
      dataEvent: EVENT_CATEGORY_LESSON_PLAN,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add Lesson Plan',
      deleteButtonText: 'Delete Lesson Plan',
      deleteAlertText: 'Are you sure you want to delete this lesson plan?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new lesson plan is added.',
      placeholderText: 'Drag and drop a Lesson Plan here to upload it.',
      documentType: LESSON_PLAN_DOCUMENT,
      canBookmark: false,
    },
    {
      key: 'printableWorksheet',
      name: 'Worksheets',
      dataEvent: EVENT_CATEGORY_WORKSHEETS,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add Worksheet',
      deleteButtonText: 'Delete Worksheet',
      deleteAlertText: 'Are you sure you want to delete these worksheets?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until new worksheets are added.',
      placeholderText: 'Drag and drop a Worksheet here to upload it.',
      documentType: WORKSHEET_DOCUMENT,
      canBookmark: false,
    },
    {
      key: 'printableAnswerSheet',
      name: 'Answers',
      dataEvent: EVENT_CATEGORY_ANSWER_SHEET,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add Answer Sheet',
      deleteButtonText: 'Delete Answers',
      deleteAlertText: 'Are you sure you want to delete these answers?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until new answers are added.',
      placeholderText: 'Drag and drop an Answer Sheet here to upload it.',
      documentType: ANSWER_SHEET_DOCUMENT,
      canBookmark: false,
    },
    {
      key: 'printableSupportSheet',
      name: 'Support',
      dataEvent: EVENT_CATEGORY_SUPPORT_DOCUMENT,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add Support Sheet',
      deleteButtonText: 'Delete Support Sheet',
      deleteAlertText: 'Are you sure you want to delete this Support Sheet?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new Support Sheet is added.',
      placeholderText: 'Drag and drop a Support Sheet here to upload it.',
      documentType: SUPPORT_DOCUMENT,
      canBookmark: false,
    },
    {
      key: 'printableExtensionSheet',
      name: 'Extension',
      dataEvent: EVENT_CATEGORY_EXTENSION_DOCUMENT,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add Extension Sheet',
      deleteButtonText: 'Delete Extension Sheet',
      deleteAlertText: 'Are you sure you want to delete this Extension Sheet?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new Extension Sheet is added.',
      placeholderText: 'Drag and drop an Extension Sheet here to upload it.',
      documentType: EXTENSION_DOCUMENT,
      canBookmark: false,
    },
    {
      key: 'printableVocabularyEnSheet',
      name: 'Vocabulary - English',
      dataEvent: EVENT_CATEGORY_VOCABULARY_EN_DOCUMENT,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add English Vocabulary Sheet',
      deleteButtonText: 'Delete English Vocabulary Sheet',
      deleteAlertText:
        'Are you sure you want to delete this English Vocabulary Sheet?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new English Vocabulary Sheet is added.',
      placeholderText:
        'Drag and drop an English Vocabulary Sheet here to upload it.',
      documentType: VOCABULARY_EN_DOCUMENT,
      canBookmark: true,
      regions: [REGION_US],
    },
    {
      key: 'printableVocabularyEsSheet',
      name: 'Vocabulary - Spanish',
      dataEvent: EVENT_CATEGORY_VOCABULARY_ES_DOCUMENT,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add Spanish Vocabulary Sheet',
      deleteButtonText: 'Delete Spanish Vocabulary Sheet',
      deleteAlertText:
        'Are you sure you want to delete this Spanish Vocabulary Sheet?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new Spanish Vocabulary Sheet is added.',
      placeholderText:
        'Drag and drop a Spanish Vocabulary Sheet here to upload it.',
      documentType: VOCABULARY_ES_DOCUMENT,
      canBookmark: true,
      regions: [REGION_US],
    },
    {
      key: 'printableTeacherInformationSheet',
      name: 'Teacher Information',
      dataEvent: EVENT_CATEGORY_TEACHER_INFORMATION_DOCUMENT,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add Teacher Information',
      deleteButtonText: 'Delete Teacher Information',
      deleteAlertText:
        'Are you sure you want to delete this Teacher Information?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until new Teacher Information is added.',
      placeholderText: 'Drag and drop Teacher Information here to upload it.',
      documentType: TEACHER_INFORMATION_DOCUMENT,
      canBookmark: false,
      regions: [REGION_AU],
    },
    {
      key: 'printableLessonRubricSheet',
      name: 'Rubric',
      dataEvent: EVENT_CATEGORY_LESSON_RUBRIC_DOCUMENT,
      displaySingle: true,
      hasSort: false,
      addButtonText: 'Add Rubric',
      deleteButtonText: 'Delete Rubric',
      deleteAlertText: 'Are you sure you want to delete this Rubric?',
      deleteAlertTextSecondary:
        'Any shared links will be broken until a new Rubric is added.',
      placeholderText: 'Drag and drop a Rubric here to upload it.',
      documentType: LESSON_RUBRIC_DOCUMENT,
      canBookmark: false,
      regions: [REGION_AU],
    },
  ],
  [ASSESSMENT]: [],
};

export const STIMULUS = 'stimulus';
export const PRINTABLES = 'printables';
export const PREVIOUS = 'previous-version';

export const LESSON_TABS = [
  {
    label: 'Lesson',
    value: 'lesson',
  },
  {
    label: 'Stimulus',
    value: STIMULUS,
  },
  {
    label: 'Printables',
    value: PRINTABLES,
  },
  {
    label: 'Previous Version',
    value: PREVIOUS,
  },
];

export const SPANISH_SUPPORT_FLAG = 'spanishSupported';
